import { Mic, Play, RotateCcw, Square, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  AIM_SECONDS, READING, STOP_SECONDS, cannotRecord, clock, ownVoiceLine, recordingLine, speaksLine,
  takeProblem,
} from "@/lib/own-voice";
import { hearOwnVoice, useDeleteOwnVoice, useMyVoice, useRecordOwnVoice } from "@/lib/own-voice-api";
import {
  microphoneProblem, recordingSupport, startRecording, type Recording, type Take,
} from "@/lib/own-voice-recorder";
import type { MyVoice } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * A person's own voice, on their Account page (ADR-117 in `platform`): whose voice speaks their
 * voicemail greetings, and the way to make it their own.
 *
 * Only the person records it. They read a text aloud for about a minute, listen to the take, tick
 * the agreement core-api words, and send it; core-api has ElevenLabs make a voice from it, and
 * that voice speaks their greetings from the next call on. They can hear a sentence in it, record
 * again, and delete it. A session an admin opened as them cannot do any of it: it is their voice
 * and their yes.
 *
 * The recording never leaves the browser until the person sends it, and nothing keeps it after.
 */
export function OwnVoiceSection({ signedInAs }: { signedInAs: boolean }) {
  const toast = useToast();
  const { data: mine, error } = useMyVoice();
  const remove = useDeleteOwnVoice();
  const [recording, setRecording] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [hearing, setHearing] = React.useState(false);

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof ApiError ? error.message : "Could not read which voice speaks for you."}
      </Callout>
    );
  }
  if (!mine) return null;
  const blocked = cannotRecord(mine, recordingSupport(), signedInAs);

  const hear = async () => {
    setHearing(true);
    try {
      const url = URL.createObjectURL(await hearOwnVoice());
      const audio = new Audio(url);
      audio.addEventListener("ended", () => URL.revokeObjectURL(url), { once: true });
      await audio.play();
    } catch (e) {
      toast.error("Could not play your voice", e instanceof ApiError ? e.message : "Please try again.");
    } finally {
      setHearing(false);
    }
  };

  const erase = async () => {
    try {
      await remove.mutateAsync();
      setDeleting(false);
      toast.success("Your voice was deleted", "Your team's voice speaks for you again.");
    } catch (e) {
      toast.error("Could not delete your voice", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <p className="text-sm font-medium text-text">Your own voice</p>
      <p className="text-xs text-text-muted">
        {speaksLine(mine)}{" "}
        {mine.own_voice
          ? ownVoiceLine(mine.own_voice, formatDateTime)
          : "Record yourself reading for a minute, and your greetings are spoken in a copy of your voice."}
      </p>
      {blocked && !mine.own_voice && <p className="text-xs text-text-muted">{blocked}</p>}
      <div className="flex flex-wrap gap-2">
        {mine.own_voice && (
          <Button type="button" variant="outline" size="sm" loading={hearing} onClick={() => void hear()}>
            <Play />
            Hear it
          </Button>
        )}
        <Button
          type="button"
          variant={mine.own_voice ? "outline" : "default"}
          size="sm"
          disabled={blocked !== null}
          title={blocked ?? undefined}
          onClick={() => setRecording(true)}
        >
          <Mic />
          {mine.own_voice ? "Record again" : "Record my voice"}
        </Button>
        {mine.own_voice && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={signedInAs}
            onClick={() => setDeleting(true)}
          >
            <Trash2 />
            Delete my voice
          </Button>
        )}
      </div>

      <RecordVoiceDialog open={recording} onOpenChange={setRecording} mine={mine} />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="Delete your voice?"
        description="Your voice stops speaking your greetings at once and is deleted at ElevenLabs. Your team's voice speaks for you again. You can record a new one at any time."
        confirmLabel="Delete my voice"
        destructive
        loading={remove.isPending}
        onConfirm={() => void erase()}
      />
    </div>
  );
}

type Step = "read" | "recording" | "working" | "listen";

/**
 * Record, listen, agree, send. The dialog holds one take at a time and lets it go when it closes:
 * a take that was not sent is nowhere afterwards.
 */
function RecordVoiceDialog({
  open, onOpenChange, mine,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mine: MyVoice;
}) {
  const toast = useToast();
  const send = useRecordOwnVoice();
  const [step, setStep] = React.useState<Step>("read");
  const [language, setLanguage] = React.useState<keyof typeof READING>("en");
  const [seconds, setSeconds] = React.useState(0);
  const [level, setLevel] = React.useState(0);
  const [take, setTake] = React.useState<Take | null>(null);
  const [agreed, setAgreed] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const live = React.useRef<Recording | null>(null);
  const kept = React.useRef<Take | null>(null);

  const letGo = React.useCallback(() => {
    live.current?.cancel();
    live.current = null;
    kept.current?.release();
    kept.current = null;
  }, []);

  // Closed, or gone from the page: the microphone is let go and the take with it.
  React.useEffect(() => {
    if (open) return;
    letGo();
    setStep("read");
    setSeconds(0);
    setLevel(0);
    setTake(null);
    setAgreed(false);
    setProblem(null);
  }, [open, letGo]);
  React.useEffect(() => letGo, [letGo]);

  const stop = React.useCallback(async () => {
    const recording = live.current;
    if (!recording) return;
    live.current = null;
    setStep("working");
    try {
      const made = await recording.stop();
      kept.current = made;
      setTake(made);
      setStep("listen");
    } catch {
      setProblem("The recording could not be read. Record it again.");
      setStep("read");
    }
  }, []);

  // The clock, and a stop short of the limit so a take is never refused for its length.
  React.useEffect(() => {
    if (step !== "recording") return;
    const started = Date.now();
    const timer = window.setInterval(() => {
      const now = (Date.now() - started) / 1000;
      setSeconds(now);
      if (now >= STOP_SECONDS) void stop();
    }, 200);
    return () => window.clearInterval(timer);
  }, [step, stop]);

  const start = async () => {
    setProblem(null);
    kept.current?.release();
    kept.current = null;
    setTake(null);
    setAgreed(false);
    setSeconds(0);
    try {
      live.current = await startRecording(setLevel);
      setStep("recording");
    } catch (e) {
      setProblem(microphoneProblem(e));
      setStep("read");
    }
  };

  const useIt = async () => {
    if (!take) return;
    setProblem(null);
    try {
      await send.mutateAsync({ wav: take.wav, agreed });
      toast.success("Your voice is ready", "Your voicemail greetings are spoken in it from the next call on.");
      onOpenChange(false);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : "Could not make your voice. Please try again.");
    }
  };

  const tooShortOrLong = take ? takeProblem(take.seconds, mine) : null;
  const reading = READING[language];

  return (
    <Dialog open={open} onOpenChange={(next) => (send.isPending ? undefined : onOpenChange(next))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record your voice</DialogTitle>
          <DialogDescription>
            Read the text aloud, at the pace you speak to a lead. About a minute is enough. Use the headset you take
            calls with, somewhere quiet.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {problem && <Callout tone="danger">{problem}</Callout>}

          {step !== "listen" && (
            <div className="space-y-1.5">
              <Tabs value={language} onValueChange={(v) => setLanguage(v as keyof typeof READING)}>
                <TabsList aria-label="The language to read in">
                  {(Object.keys(READING) as (keyof typeof READING)[]).map((code) => (
                    <TabsTrigger key={code} value={code} disabled={step !== "read"}>
                      {READING[code].label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <blockquote
                lang={language}
                className="max-h-56 overflow-y-auto rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm leading-relaxed text-text"
              >
                {reading.text}
              </blockquote>
              <p className="text-xs text-text-muted">
                Read it in the language you speak on calls. Your voice then speaks greetings in every language.
              </p>
            </div>
          )}

          {step === "recording" && (
            <div className="space-y-1.5" role="status" aria-live="polite">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums text-danger-11">
                  <span className="size-2 animate-pulse rounded-full bg-danger-9" aria-hidden />
                  {clock(seconds)}
                </span>
                <span
                  className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-sunken"
                  role="meter"
                  aria-label="How loud the microphone hears you"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(level * 100)}
                >
                  <span
                    className={cn("block h-full rounded-full transition-[width] duration-100", level > 0.05 ? "bg-success-9" : "bg-border-strong")}
                    style={{ width: `${Math.round(level * 100)}%` }}
                  />
                </span>
                <span className="text-xs tabular-nums text-text-subtle">aim for {clock(AIM_SECONDS)}</span>
              </div>
              <p className="text-xs text-text-muted">{recordingLine(seconds, mine.min_seconds)}</p>
            </div>
          )}

          {step === "working" && <p className="text-sm text-text-muted">Getting the recording ready…</p>}

          {step === "listen" && take && (
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-text">Listen to it first · {clock(take.seconds)}</p>
                <audio src={take.url} controls className="h-9 w-full" aria-label="Your recording" />
              </div>
              {tooShortOrLong ? (
                <Callout tone="warning">{tooShortOrLong}</Callout>
              ) : (
                <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-surface px-3 py-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0 accent-[var(--accent-9)]"
                    checked={agreed}
                    disabled={send.isPending}
                    onChange={(e) => setAgreed(e.target.checked)}
                  />
                  <span className="text-sm text-text">{mine.agreement}</span>
                </label>
              )}
              <p className="text-xs text-text-muted">
                The recording is sent to ElevenLabs to make your voice, and is not kept here. Making the voice takes a
                few seconds.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={send.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {step === "read" && (
            <Button type="button" variant="default" onClick={() => void start()}>
              <Mic />
              Start recording
            </Button>
          )}
          {step === "recording" && (
            <Button type="button" variant="danger" onClick={() => void stop()}>
              <Square />
              Stop
            </Button>
          )}
          {step === "listen" && (
            <>
              <Button type="button" variant="outline" disabled={send.isPending} onClick={() => void start()}>
                <RotateCcw />
                Record again
              </Button>
              <Button
                type="button"
                variant="default"
                loading={send.isPending}
                disabled={!agreed || tooShortOrLong !== null}
                onClick={() => void useIt()}
              >
                Make my voice from this
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
